import { isMobile } from '@direct-frontend/platform-code';
import { forwardRef, type ReactNode } from 'react';

import { VerticalAlignAddonSize, VerticalAlignTextPreset } from '../../../hooks/useVerticalAlign/types';
import { useInternalVerticalAlign } from '../../../hooks/useVerticalAlign/useVerticalAlign';
import { IconArrowRight } from '../../../icons';
import { cn } from '../../../lib/css/className/className';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import { ClickableIcon } from '../../ClickableIcon';
import { VStack } from '../../Stack';
import { SidebarItem, type SidebarItemProps } from '../SidebarItem';
import './SidebarCollapseItem.css';

export const cnSidebarCollapseItem = cn('SidebarCollapseItem');

const ItemTextSize: VerticalAlignTextPreset = '14-20';
const ItemAddonSize: VerticalAlignAddonSize = '16';

export type SidebarCollapseItemProps = Omit<SidebarItemProps, 'addonRight' | 'children'> & {
    /**
     * Раскрыто ли collapse-меню (подменю вниз)
     */
    isExpanded: boolean;
    /**
     * Метод для переключения состояния раскрытия collapse-меню (подменю вниз)
     */
    onToggleExpanded: VoidFunction;

    /**
     * Элементы для отображения в подменю при раскрытом состоянии
     * @expectedNode `SidebarItem` | `SidebarItemStub`
     */
    children?: ReactNode;
};

/**
 * Sidebar Item для отображения пункта меню с возможностью раскрытия и скрытия подменю
 */
export const SidebarCollapseItem = forwardRef<HTMLElement, SidebarCollapseItemProps>(
    function SidebarCollapseItem(props, ref) {
        const { isExpanded, onToggleExpanded, children, ...restProps } = props;
        const { 'data-testid': dataTestId } = restProps;

        const { addonClasses: alignAddonClass } = useInternalVerticalAlign({
            textSize: ItemTextSize,
            addonSize: ItemAddonSize,
        });

        if (isMobile()) {
            return (
                <SidebarItem ref={ref} {...restProps}>
                    {children}
                </SidebarItem>
            );
        }

        const hasChildren = Boolean(children);
        const sizeSkinClass = getSizeSkin('s');

        return (
            <>
                <SidebarItem
                    ref={ref}
                    {...restProps}
                    shouldContentGrow={hasChildren}
                    className={cnSidebarCollapseItem('', [
                        restProps.className,
                        ...(hasChildren ? [sizeSkinClass] : []),
                    ])}
                    addonRight={
                        hasChildren ? (
                            <ClickableIcon
                                color="gray"
                                clickableAreaSize={'36'}
                                onClick={onToggleExpanded}
                                data-testid={dataTestId ? `${dataTestId}.ExpandButton` : undefined}
                                className={cnSidebarCollapseItem('expandButton')}
                            >
                                <IconArrowRight
                                    className={cnSidebarCollapseItem('expandIcon', { expanded: isExpanded }, [
                                        alignAddonClass,
                                    ])}
                                    size="16"
                                    data-testid={dataTestId ? `${dataTestId}.ExpandIcon` : undefined}
                                />
                            </ClickableIcon>
                        ) : undefined
                    }
                />
                {hasChildren ? (
                    <div
                        className={cnSidebarCollapseItem('subMenuContainer', {
                            expanded: isExpanded,
                        })}
                    >
                        <VStack className={cnSidebarCollapseItem('subMenuInner')}>{children}</VStack>
                    </div>
                ) : null}
            </>
        );
    },
);
